import { Link } from 'react-router-dom';
import { BookOpen, CreditCard, Headphones, LifeBuoy, Mail } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Seo } from '../components/Seo';
import { COMFORT_ENGINE_VERSION } from '../lib/comfortCalc';

export function HelpPage() {
  return (
    <>
      <Seo
        path="/help"
        title="Help en uitleg"
        description="Hoe ComfortTool™ werkt: de scan, resultaat, weer per uur, Comfort Basis en Plus, account, aankopen, downloadbaar overzicht, installatieadviseur en printen als PDF."
        keywords="ComfortTool help, uitleg, scan, rapport, account, warmte, koeling"
      />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:py-14 text-ct-text">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Help' }]} />
        <header className="mt-6">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Help &amp; uitleg</h1>
          <p className="mt-3 text-base text-ct-muted">
            Alles om snel productief te worden — en waar je terechtkunt als iets hapert.
          </p>
        </header>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <Link
            to="/faq"
            className="flex gap-3 rounded-2xl border border-ct-border bg-ct-surface/90 p-4 transition hover:border-ct-accent/35"
          >
            <BookOpen className="h-8 w-8 shrink-0 text-ct-accent" aria-hidden />
            <div>
              <p className="font-bold text-ct-text">FAQ</p>
              <p className="mt-1 text-sm text-ct-muted">Alle veelgestelde vragen per onderwerp.</p>
            </div>
          </Link>
          <Link
            to="/troubleshooting"
            className="flex gap-3 rounded-2xl border border-ct-border bg-ct-surface/90 p-4 transition hover:border-ct-accent/35"
          >
            <LifeBuoy className="h-8 w-8 shrink-0 text-ct-accent" aria-hidden />
            <div>
              <p className="font-bold text-ct-text">Troubleshooting</p>
              <p className="mt-1 text-sm text-ct-muted">Internet, je apparaat, inloggen en betalen.</p>
            </div>
          </Link>
          <Link
            to="/betaalmethoden"
            className="flex gap-3 rounded-2xl border border-ct-border bg-ct-surface/90 p-4 transition hover:border-ct-accent/35"
          >
            <CreditCard className="h-8 w-8 shrink-0 text-ct-accent" aria-hidden />
            <div>
              <p className="font-bold text-ct-text">Betaalmethoden</p>
              <p className="mt-1 text-sm text-ct-muted">Welke opties je kunt gebruiken — met uitleg en iconen.</p>
            </div>
          </Link>
          <div className="flex gap-3 rounded-2xl border border-ct-border bg-ct-elevated/40 p-4">
            <Headphones className="h-8 w-8 shrink-0 text-ct-accent" aria-hidden />
            <div>
              <p className="font-bold text-ct-text">Installatieadviseur</p>
              <p className="mt-1 text-sm text-ct-muted">
                Digitale adviseur (rechtsonder) voor korte vragen over de scan — als die optie actief is. Anders: neem
                contact op.
              </p>
            </div>
          </div>
          <Link
            to="/contact"
            className="flex gap-3 rounded-2xl border border-ct-border bg-ct-surface/90 p-4 transition hover:border-ct-accent/35"
          >
            <Mail className="h-8 w-8 shrink-0 text-ct-accent" aria-hidden />
            <div>
              <p className="font-bold text-ct-text">Contact</p>
              <p className="mt-1 text-sm text-ct-muted">Persoonlijk antwoord van het team — ook voor vragen over je situatie.</p>
            </div>
          </Link>
        </div>

        <div className="mt-12 space-y-8 text-sm leading-relaxed text-ct-muted">
          <section className="rounded-2xl border border-ct-border bg-ct-surface/90 p-5 sm:p-6">
            <h2 className="text-lg font-bold text-ct-text">Zo loopt de site</h2>
            <ol className="mt-4 list-inside list-decimal space-y-2">
              <li>
                <strong className="text-ct-text">Home</strong> — overzicht, journey en links naar{' '}
                <Link to="/prijzen" className="font-medium text-ct-accent hover:underline">
                  prijzen
                </Link>
                .
              </li>
              <li>
                <strong className="text-ct-text">Scan</strong> (
                <Link to="/scan" className="font-medium text-ct-accent hover:underline">
                  /scan
                </Link>
                ) — segment, gebouw en kamers; je voortgang blijft op je apparaat bewaard.
              </li>
              <li>
                <strong className="text-ct-text">Resultaat</strong> — indicatieve vermogens en jaarschatting.
              </li>
              <li>
                <strong className="text-ct-text">Uurlijks (optioneel)</strong> — extra check met weer uit het verleden
                (ComfortTool haalt dat voor je op).
              </li>
              <li>
                <strong className="text-ct-text">Adviesrapport (optioneel)</strong> — Comfort Basis (vaste opbouw) of
                Comfort Plus (uitgebreider leesbaar rapport).
              </li>
              <li>
                <strong className="text-ct-text">Printrapport</strong> —{' '}
                <code className="rounded bg-ct-elevated px-1.5 py-0.5 text-ct-accent">/rapport</code> voor print/PDF.
              </li>
            </ol>
          </section>
          <section className="rounded-2xl border border-ct-border bg-ct-surface/90 p-5 sm:p-6">
            <h2 className="text-lg font-bold text-ct-text">Calculatie vs. rapporttekst</h2>
            <p className="mt-3">
              De <strong className="text-ct-text">cijfers</strong> komen uit de rekenkern (versie{' '}
              <span className="font-mono text-ct-text">{COMFORT_ENGINE_VERSION}</span>). Het{' '}
              <strong className="text-ct-text">uitgeschreven advies</strong> (Plus) is een taalmodel-samenvatting: nuttig
              om te lezen, maar altijd naast de cijfers en het oordeel van een professional op locatie leggen.
            </p>
          </section>
          <section className="rounded-2xl border border-ct-border bg-ct-surface/90 p-5 sm:p-6">
            <h2 className="text-lg font-bold text-ct-text">Account en aankoop</h2>
            <ul className="mt-3 list-inside list-disc space-y-2">
              <li>
                Registreren of inloggen via{' '}
                <Link to="/registreren" className="font-medium text-ct-accent hover:underline">
                  /registreren
                </Link>{' '}
                en{' '}
                <Link to="/inloggen" className="font-medium text-ct-accent hover:underline">
                  /inloggen
                </Link>
                .
              </li>
              <li>
                Koppel je aankoop: gebruik het <strong className="text-ct-text">zelfde e-mailadres</strong> als bij de
                betaling, of volg de stappen op de bedanktpagina na je aankoop.
              </li>
              <li>
                <Link to="/wachtwoord-vergeten" className="font-medium text-ct-accent hover:underline">
                  Wachtwoord vergeten
                </Link>{' '}
                — alleen als wachtwoord-mail voor jouw omgeving is ingeschakeld.
              </li>
              <li>
                In je account: <strong className="text-ct-text">downloadbaar overzicht</strong> (tabel) per aankoop waar
                dat beschikbaar is.
              </li>
            </ul>
          </section>
          <section className="rounded-2xl border border-ct-border bg-ct-surface/90 p-5 sm:p-6">
            <h2 className="text-lg font-bold text-ct-text">Privacy &amp; juridisch</h2>
            <ul className="mt-3 list-inside list-disc space-y-2">
              <li>
                <Link to="/privacy" className="font-medium text-ct-accent hover:underline">
                  Privacy
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="font-medium text-ct-accent hover:underline">
                  Cookies
                </Link>
              </li>
              <li>
                <Link to="/voorwaarden" className="font-medium text-ct-accent hover:underline">
                  Algemene voorwaarden
                </Link>
                ,{' '}
                <Link to="/bestelvoorwaarden" className="font-medium text-ct-accent hover:underline">
                  bestelvoorwaarden
                </Link>
                ,{' '}
                <Link to="/disclaimer" className="font-medium text-ct-accent hover:underline">
                  disclaimer
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </>
  );
}
